/**
 * Dashboard entry point.
 *
 * Bootstrap sequence (WebSocket-authoritative model):
 *   1. Gate behind a valid auth-svc session (inline login overlay).
 *   2. Mount the app shell (sidebar + header + router host).
 *   3. Wire the single WebSocket to the store and the raw-telemetry buffer.
 *   4. Fetch initial REST data, connect the socket, navigate to the default view.
 *
 * All page structure now lives in {@link module:app/shell} and the per-section
 * views under `features/`. This file only owns the bootstrap wiring.
 * @module main
 */

import { registerNavigator } from "./app/navigate.ts";
import { mapHost, ws } from "./app/services.ts";
import { mountShell } from "./app/shell.ts";
import { apiFetch, getMe, type MeResponse, signIn } from "./auth.ts";
import { loadTenants, tenantId } from "./features/new-dashboard/tenant.ts";
import { store } from "./state/store.ts";
import type {
	Alert,
	FleetOverview,
	VehicleSnapshot,
} from "./types/telemetry.ts";

/** Lazily-loaded raw-telemetry module (warmed in the background after login). */
type RawTelMod = typeof import("./panels/raw-telemetry.ts");
let rawTelMod: RawTelMod | null = null;

/** Route the single WebSocket into the store and the raw-telemetry buffer. */
function wireWebSocket(): void {
	ws.on("*", (event) => store.handleWSEvent(event));
	ws.on("telemetry.update", (event) => {
		const snap = event.payload as VehicleSnapshot | undefined;
		if (snap?.last_telemetry) {
			rawTelMod?.pushRawTelemetry(snap.last_telemetry);
		}
	});
}

/** Fetch active/idle vehicle-hours from the backend. */
async function refreshUtilization(): Promise<void> {
	try {
		const tid = tenantId();
		const tQuery = tid ? `?tenant_id=${encodeURIComponent(tid)}` : "";
		const res = await apiFetch(`/api/v1/fleet/utilization${tQuery}`);
		if (res.ok) {
			const util: { active_hours: number; idle_hours: number } =
				await res.json();
			store.seedUtilization(util.active_hours, util.idle_hours);
		}
	} catch (err) {
		console.error("Failed to refresh utilization:", err);
	}
}

/** Seed the store from REST so data survives a page refresh. */
async function fetchInitialData(): Promise<void> {
	try {
		const tid = tenantId();
		const tQuery = tid ? `?tenant_id=${encodeURIComponent(tid)}` : "";
		const [overviewRes, alertsRes, vehiclesRes] = await Promise.all([
			apiFetch(`/api/v1/fleet/overview${tQuery}`),
			apiFetch(`/api/v1/alerts${tQuery}`),
			apiFetch(`/api/v1/vehicles${tQuery}`),
		]);

		if (overviewRes.ok) {
			const overview: FleetOverview = await overviewRes.json();
			store.setFleetOverview(overview);
		}

		await refreshUtilization();

		if (alertsRes.ok) {
			const alerts: Alert[] = (await alertsRes.json()) ?? [];
			store.setAlerts(alerts);
		}

		if (vehiclesRes.ok) {
			const vehicles = (await vehiclesRes.json()) ?? [];
			store.setVehicleNames(vehicles);
			vehicles.forEach((v: { id: string; status: string }) => {
				store.handleWSEvent({
					type: "vehicle.status",
					payload: { vehicle_id: v.id, status: v.status },
				});
			});
		}

		const snapshotsRes = await apiFetch(`/api/v1/vehicles/snapshots${tQuery}`);
		if (snapshotsRes.ok) {
			const snapshots = (await snapshotsRes.json()) ?? [];
			snapshots.forEach((snap: VehicleSnapshot) => {
				store.handleWSEvent({
					type: "telemetry.update",
					payload: snap,
				});
			});
		}
	} catch (err) {
		console.error("Failed to fetch initial data:", err);
	}
}

/** Resolve a valid session, prompting for credentials if needed. */
async function authenticate(): Promise<MeResponse> {
	const me = await getMe();
	return me ?? promptLogin();
}

/** Render an overlay login form and resolve once the user signs in. */
function promptLogin(): Promise<MeResponse> {
	return new Promise((resolve) => {
		const overlay = document.createElement("div");
		overlay.id = "login-overlay";
		overlay.innerHTML = `
      <form id="login-form" class="login-card" autocomplete="on">
        <button id="login-theme-toggle" class="theme-btn login-theme-btn" type="button" title="Toggle light/dark mode" aria-label="Toggle light/dark mode">
          <svg id="login-theme-icon-dark" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
          <svg id="login-theme-icon-light" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:none"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
        </button>
        <div class="login-brand">
          <span class="login-logo" role="img" aria-label="Udexon"></span>
        </div>
        <div class="login-title">Sign in to Udexon</div>
        <div class="login-subtitle">Authenticate to access fleet operations control.</div>

        <div id="login-error" class="login-error" style="display:none"></div>

        <div class="login-field">
          <label for="login-input">Username or email</label>
          <input id="login-input" type="text" autocomplete="username" placeholder="admin" required />
        </div>
        <div class="login-field">
          <label for="password-input">Password</label>
          <input id="password-input" type="password" autocomplete="current-password" placeholder="••••••••" required />
        </div>

        <button id="login-submit" type="submit">Sign in</button>

        <div class="login-footer">Udexon Fleet Operations Control</div>
      </form>`;
		document.body.appendChild(overlay);

		const form = overlay.querySelector("#login-form") as HTMLFormElement;
		const loginInput = overlay.querySelector(
			"#login-input",
		) as HTMLInputElement;
		const passInput = overlay.querySelector(
			"#password-input",
		) as HTMLInputElement;
		const errEl = overlay.querySelector("#login-error") as HTMLDivElement;
		const submit = overlay.querySelector("#login-submit") as HTMLButtonElement;
		loginInput.focus();

		wireLoginThemeToggle(overlay);

		form.addEventListener("submit", async (e) => {
			e.preventDefault();
			errEl.style.display = "none";
			submit.disabled = true;
			submit.textContent = "Signing in…";
			try {
				const user = await signIn(loginInput.value.trim(), passInput.value);
				overlay.remove();
				resolve(user);
			} catch (err) {
				errEl.textContent = (err as Error).message || "Sign-in failed";
				errEl.style.display = "block";
				submit.disabled = false;
				submit.textContent = "Sign in";
			}
		});
	});
}

/** Apply the persisted theme and wire the login-screen light/dark toggle. */
function wireLoginThemeToggle(scope: HTMLElement): void {
	const btn = scope.querySelector("#login-theme-toggle");
	const iconDark = scope.querySelector(
		"#login-theme-icon-dark",
	) as HTMLElement | null;
	const iconLight = scope.querySelector(
		"#login-theme-icon-light",
	) as HTMLElement | null;
	if (!btn || !iconDark || !iconLight) return;

	const sync = (theme: string) => {
		iconDark.style.display = theme === "dark" ? "" : "none";
		iconLight.style.display = theme === "light" ? "" : "none";
	};

	const saved = localStorage.getItem("theme") === "light" ? "light" : "dark";
	document.documentElement.setAttribute("data-theme", saved);
	sync(saved);

	btn.addEventListener("click", () => {
		const isLight =
			document.documentElement.getAttribute("data-theme") === "light";
		const next = isLight ? "dark" : "light";
		document.documentElement.classList.add("theme-changing");
		document.documentElement.setAttribute("data-theme", next);
		localStorage.setItem("theme", next);
		sync(next);
		setTimeout(
			() => document.documentElement.classList.remove("theme-changing"),
			400,
		);
	});
}

/** Application bootstrap. */
async function bootstrap(): Promise<void> {
	const me = await authenticate();
	const router = mountShell(me);
	registerNavigator((routeId) => router.navigate(routeId));

	// Load tenants first so that the active tenant is resolved before fetching initial data.
	await loadTenants();

	wireWebSocket();

	// Warm the raw-telemetry buffer in the background so the Data view is instant.
	import("./panels/raw-telemetry.ts").then(async (mod) => {
		rawTelMod = mod;
		await mod.initRawTelemetry();
	});

	await fetchInitialData();
	ws.connect();

	await router.navigate("fleet/overview");

	setInterval(refreshUtilization, 10_000);
	window.addEventListener("resize", () => mapHost.resize());
}

bootstrap();
